import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Field, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCcw, Filter, ArrowRight, PackageSearch, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { fmtFecha } from "@/lib/fechas";
import { fmtAlbaranNro, fmtEnvios } from "@/lib/textos";
import SmartSearch from "@/components/SmartSearch";
import SavedViews from "@/components/SavedViews";
import { SortableTh, useTableSort } from "@/components/SortableTh";
import { useAuth } from "@/auth/AuthContext";

function fmt(n) { return typeof n === "number" ? n.toLocaleString("es-ES") : n; }

const FETCH_SIZE = 500;
const DEFAULT_FILTERS = { buscar: "", serie: "", cliente: "", fecha_desde: "", fecha_hasta: "", status_facturado: "", estado: "pendiente", transportista: "" };

function filtrosIniciales(sinSage) {
  return { ...DEFAULT_FILTERS, estado: sinSage ? "todos" : "pendiente", status_facturado: "" };
}
const CHIP_LABELS = { buscar: "Buscar", serie: "Serie", cliente: "Cliente", fecha_desde: "Desde", fecha_hasta: "Hasta", status_facturado: "Facturado", estado: "Estado", transportista: "Transportista" };
const CHIP_VALUES = { pendiente: "Pendientes", usado: "Usados en DeCA", todos: "Todos", "0": "Pendiente fact.", "-1": "Facturado", sin: "Sin transportista" };

function clienteKey(r) {
  const nif = String(r?.cliente?.nif || r?.destinatario?.nif || "").trim().toUpperCase();
  const nom = String(r?.cliente?.razon_social || r?.destinatario?.razon_social || "").trim().toLowerCase();
  if (nif) return `nif:${nif}`;
  if (nom) return `nom:${nom}`;
  return `row:${r?.ejercicio}|${r?.serie}|${r?.numero}`;
}

function transportistaKey(r) {
  const t = r?.transportista || {};
  if (t.codigo != null && t.codigo !== "" && Number(t.codigo) !== 0) return `cod:${t.codigo}`;
  if (t.nif) return `nif:${String(t.nif).trim()}`;
  if (t.razon_social) return `nom:${String(t.razon_social).trim().toLowerCase()}`;
  return "sin";
}

function displayedNro(r) {
  return fmtAlbaranNro(r?.serie, r?.numero);
}

function soloDigitos(s) {
  return String(s || "").replace(/\D/g, "");
}

function matchesBuscar(r, raw) {
  const b = (raw || "").trim().toLowerCase();
  if (!b) return true;
  const compact = b.replace(/\s+/g, "");
  const digits = soloDigitos(compact);
  const num = String(r?.numero ?? "").trim();
  const serie = String(r?.serie || "").trim();
  const shown = displayedNro(r);
  const campos = [
    num, serie, shown, `${serie}${num}`, `${serie}-${num}`,
    r.ejercicio,
    r.cliente?.razon_social, r.cliente?.nif, r.cliente?.municipio,
    r.transportista?.razon_social, r.transportista?.nif,
    r.cargador?.razon_social, r.cargador?.nif,
  ];
  if (campos.some((x) => {
    const t = String(x ?? "").toLowerCase();
    return t.includes(b) || t.replace(/\s+/g, "").includes(compact);
  })) return true;
  if (digits && (soloDigitos(num).includes(digits) || soloDigitos(shown).includes(digits))) return true;
  return false;
}

function matchesTransportista(r, raw) {
  const t = (raw || "").trim();
  if (!t) return true;
  const tra = r.transportista || {};
  const nombre = String(tra.razon_social || "").trim();
  const nif = String(tra.nif || "").trim();
  const codigo = tra.codigo;
  const tiene = Boolean(codigo) || Boolean(nombre) || Boolean(nif);
  const tl = t.toLowerCase();
  if (tl === "sin") return !tiene;
  if (tl.startsWith("cod:")) return Number(codigo) === Number(tl.slice(4));
  return nombre.toLowerCase().includes(tl) || nif.toLowerCase().includes(tl);
}

const ALBARAN_SORT = {
  nro: displayedNro,
  fecha: (r) => r.fecha || "",
  destinatario: (r) => r.cliente?.razon_social || "",
  cargador: (r) => r.cargador?.razon_social || "",
  transportista: (r) => r.transportista?.razon_social || r.transportista?.nif || "",
  bultos: (r) => Number(r.bultos) || 0,
  peso: (r) => Number(r.peso_bruto) || 0,
  estado: (r) => (r.utilizado_en_deca ? "2-usado" : r.status_facturado === -1 ? "1-facturado" : "0-pendiente"),
};

function localSuggestions(items, raw) {
  const b = (raw || "").trim().toLowerCase();
  if (b.length < 1) return [];
  const out = [];
  const seen = new Set();
  const add = (kind, label, sub, apply) => {
    const key = `${kind}|${String(label).toLowerCase()}`;
    if (!label || seen.has(key)) return;
    seen.add(key);
    out.push({ kind, label, sub, apply_filters: apply });
  };
  for (const r of items) {
    const nro = displayedNro(r);
    const numStr = String(r.numero ?? "");
    if (nro.toLowerCase().includes(b) || numStr.toLowerCase().includes(b) || (soloDigitos(b) && soloDigitos(numStr).includes(soloDigitos(b)))) {
      add("numero", nro, `Ej. ${r.ejercicio}`, { buscar: raw.trim() });
    }
    const nom = r.cliente?.razon_social || "";
    const nif = r.cliente?.nif || "";
    if (nom.toLowerCase().includes(b)) add("cliente", nom, nif ? `NIF ${nif}` : "", { cliente: nom });
    if (nif.toLowerCase().includes(b)) add("nif", nif, nom, { cliente: nif });
    const se = String(r.serie || "").trim();
    if (se && se.toLowerCase().includes(b)) add("serie", se, "Serie de albarán", { serie: se });
    const tra = r.transportista?.razon_social || "";
    const traNif = r.transportista?.nif || "";
    const traVal = r.transportista?.codigo != null && r.transportista?.codigo !== "" ? `cod:${r.transportista.codigo}` : tra;
    if (tra.toLowerCase().includes(b) || traNif.toLowerCase().includes(b)) {
      add("transportista", tra || "Sin nombre", traNif, { transportista: traVal });
    }
    if (out.length >= 8) break;
  }
  return out;
}

export default function AlbaranesPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const sinSage = user?.sage_conectado !== true;
  const [allItems, setAllItems] = useState([]);
  const [totalRemote, setTotalRemote] = useState(0);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState({ ...DEFAULT_FILTERS });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selected, setSelected] = useState(new Set());
  const [modo, setModo] = useState(user?.modo_agrupacion === "agrupado" ? "agrupado" : "unico");
  const [transportistas, setTransportistas] = useState([]);
  const loadedEstado = useRef("pendiente");

  const extraFiltersActive = Object.entries(q).some(([k, v]) => {
    if (sinSage && (k === "estado" || k === "status_facturado")) return false;
    if (k === "estado") return v && v !== "pendiente";
    return v !== "";
  });

  const load = async (estadoOverride = null) => {
    setLoading(true);
    try {
      const estado = estadoOverride ?? q.estado;
      const params = { page: 1, page_size: FETCH_SIZE };
      if (estado && estado !== "todos") params.estado = estado;
      const { data } = await api.get("/api/v1/albaranes", { params });
      setAllItems(data.items || []);
      setSource(data.source || "");
      setTotalRemote(data.remote_total ?? data.total ?? 0);
      loadedEstado.current = estado || "pendiente";
      setPage(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (next) => {
    const limpio = sinSage ? { ...next, estado: "todos", status_facturado: "" } : next;
    const prevEstado = loadedEstado.current || "pendiente";
    const nextEstado = limpio.estado || "pendiente";
    setQ(limpio);
    setPage(1);
    if (nextEstado !== prevEstado) load(nextEstado);
  };

  useEffect(() => {
    if (!user) return;
    setModo(user.modo_agrupacion === "agrupado" ? "agrupado" : "unico");
    const ini = filtrosIniciales(sinSage);
    setQ(ini);
    load(ini.estado);
    api.get("/api/v1/transportistas")
      .then(({ data }) => setTransportistas(data.items || []))
      .catch(() => setTransportistas([]));
  }, [sinSage, user?.id]); // eslint-disable-line

  const filtered = useMemo(() => allItems.filter((r) => {
    if (q.serie && !String(r.serie || "").trim().toLowerCase().includes(String(q.serie).trim().toLowerCase())) return false;
    if (q.fecha_desde && (r.fecha || "") < q.fecha_desde) return false;
    if (q.fecha_hasta && (r.fecha || "") > q.fecha_hasta) return false;
    if (!sinSage && q.status_facturado !== "" && String(r.status_facturado) !== String(q.status_facturado)) return false;
    if (q.cliente) {
      const cl = q.cliente.toLowerCase();
      const nom = String(r.cliente?.razon_social || "").toLowerCase();
      const nif = String(r.cliente?.nif || "").toLowerCase();
      if (!nom.includes(cl) && !nif.includes(cl)) return false;
    }
    if (q.transportista && !matchesTransportista(r, q.transportista)) return false;
    if (q.buscar && !matchesBuscar(r, q.buscar)) return false;
    return true;
  }), [allItems, q, sinSage]);

  const { sorted: sortedFiltered, sort, toggle: toggleSort } = useTableSort(filtered, ALBARAN_SORT);
  const pageCount = Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const items = sortedFiltered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const hasMore = safePage < pageCount;

  const keyOf = (r) => `${r.ejercicio}|${String(r.serie || "").trim()}|${r.numero}`;
  const puedeReusar = !!user?.albaran_varios_decas;
  const estaUsado = (r) => !!r.utilizado_en_deca;
  const esManualSage = (r) => !sinSage && r.fuente === "manual";
  const msgManualSage = "Este albar" + String.fromCharCode(0xe1) + "n es de un DeCA manual: no existe en Sage y no se puede seleccionar aqu" + String.fromCharCode(0xed) + ".";
  const toggle = (r) => {
    if (esManualSage(r)) {
      toast.warning(msgManualSage);
      return;
    }
    if (estaUsado(r) && !puedeReusar) return;
    const k = keyOf(r);
    const s = new Set(selected);
    if (s.has(k)) s.delete(k);
    else {
      if (estaUsado(r) && puedeReusar) {
        toast.warning("Este albar" + String.fromCharCode(0xe1) + "n ya est" + String.fromCharCode(0xe1) + " en un DeCA. Puedes reutilizarlo, pero revisa que no sea un error.");
      }
      s.add(k);
    }
    setSelected(s);
  };
  const selectableItems = items.filter((r) => !esManualSage(r) && (puedeReusar || !estaUsado(r)));
  const allChecked = selectableItems.length > 0 && selectableItems.every((r) => selected.has(keyOf(r)));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else {
      const usados = selectableItems.filter(estaUsado);
      if (usados.length) {
        toast.warning(usados.length === 1
          ? "Hay 1 albar" + String.fromCharCode(0xe1) + "n que ya est" + String.fromCharCode(0xe1) + " en un DeCA. Puedes reutilizarlo, pero revisa que no sea un error."
          : "Hay " + usados.length + " albaranes que ya est" + String.fromCharCode(0xe1) + "n en un DeCA. Puedes reutilizarlos, pero revisa que no sea un error.");
      }
      setSelected(new Set(selectableItems.map(keyOf)));
    }
  };

  const selectedRows = useMemo(
    () => filtered.filter((r) => selected.has(keyOf(r)) && !esManualSage(r) && (puedeReusar || !r.utilizado_en_deca)),
    [filtered, selected, puedeReusar, sinSage],
  );
  const selectedRefs = useMemo(
    () => selectedRows.map((r) => ({ ejercicio: r.ejercicio, serie: String(r.serie || "").trim(), numero: r.numero })),
    [selectedRows],
  );
  const mezclaTransportistas = useMemo(() => {
    const keys = new Set(selectedRows.map(transportistaKey));
    return keys.size > 1;
  }, [selectedRows]);

  const transportistaOptions = useMemo(() => {
    const map = new Map();
    for (const t of transportistas) {
      const value = t.codigo != null && t.codigo !== "" ? `cod:${t.codigo}` : t.razon_social;
      if (value) map.set(String(value), t);
    }
    for (const r of allItems) {
      const t = r.transportista || {};
      if (!t.codigo && !t.razon_social) continue;
      const value = t.codigo != null && t.codigo !== "" ? `cod:${t.codigo}` : t.razon_social;
      if (value && !map.has(String(value))) map.set(String(value), t);
    }
    return [...map.entries()]
      .map(([value, t]) => ({ value, razon_social: t.razon_social || "", nif: t.nif || "" }))
      .sort((a, b) => a.razon_social.localeCompare(b.razon_social, "es"));
  }, [transportistas, allItems]);

  const getSuggestions = useCallback((value) => localSuggestions(allItems, value), [allItems]);

  const clavesInfo = useMemo(() => {
    if (modo === "unico") {
      if (!selectedRows.length) return [];
      return [{
        cliente: { razon_social: "Un solo DeCA", nif: selectedRows.length + " albaranes" },
        n: selectedRows.length,
        unico: true,
      }];
    }
    const map = new Map();
    for (const r of selectedRows) {
      const key = clienteKey(r);
      const bucket = map.get(key) || { cliente: r.cliente, n: 0 };
      bucket.n += 1;
      map.set(key, bucket);
    }
    return [...map.values()];
  }, [selectedRows, modo]);

  const irAPreview = () => {
    if (selectedRows.length === 0) return toast.info("Selecciona al menos un albar" + String.fromCharCode(0xe1) + "n pendiente");
    const usadosSel = selectedRows.filter(estaUsado);
    if (!puedeReusar && usadosSel.length) {
      return toast.error("Hay albaranes ya usados en un DeCA. Qu" + String.fromCharCode(0xed) + "talos de la selecci" + String.fromCharCode(0xf3) + "n.");
    }
    if (mezclaTransportistas) return toast.error("Selecciona albaranes del mismo transportista");
    const manuals = selectedRows.filter(esManualSage);
    if (manuals.length) {
      return toast.error("Hay albaranes de DeCA manual. No existen en Sage: qu" + String.fromCharCode(0xed) + "talos de la selecci" + String.fromCharCode(0xf3) + "n.");
    }
    if (usadosSel.length) {
      toast.warning(usadosSel.length === 1
        ? "1 albar" + String.fromCharCode(0xe1) + "n seleccionado ya est" + String.fromCharCode(0xe1) + " en otro DeCA."
        : usadosSel.length + " albaranes seleccionados ya est" + String.fromCharCode(0xe1) + "n en otro DeCA.");
    }
    sessionStorage.setItem("deca_preview_payload", JSON.stringify({
      modo,
      albaranes: selectedRows.map((r) => ({
        ejercicio: r.ejercicio,
        serie: String(r.serie || "").trim(),
        numero: r.numero,
        fuente: r.fuente || undefined,
      })),
    }));
    nav("/decas/preview");
  };

  return (
    <div>
      <PageHeader
        title="Selección de albaranes"
        subtitle={sinSage
          ? "Albaranes de DeCA manual de tu empresa. Todos juntos en un DeCA, o agrupados por destino."
          : "Filtra y selecciona los albaranes de Sage. Los de DeCA manual no se pueden elegir: no existen en Sage."}
        testid="page-albaranes"
        actions={<>
          <SavedViews
            scope="albaranes"
            currentFilters={q}
            pageSize={pageSize}
            onApply={(v) => applyFilters({ ...filtrosIniciales(sinSage), ...(v.filters || {}) })}
          />
          <Button variant="outline" onClick={()=>load()} disabled={loading} data-testid="btn-reload"><RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}/> Actualizar</Button>
        </>}
      />

      {extraFiltersActive ? (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
          <span className="label-tag">Filtros activos:</span>
          {Object.entries(q).filter(([k, v]) => {
            if (sinSage && (k === "estado" || k === "status_facturado")) return false;
            return v !== "" && !(k === "estado" && v === "pendiente");
          }).map(([k, v]) => (
            <button
              key={k}
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 text-blue-800 text-xs px-2.5 py-1 hover:bg-blue-100"
              onClick={() => applyFilters({ ...q, [k]: k === "estado" ? "pendiente" : "" })}
              data-testid={`chip-${k}`}
            >
              <span className="uppercase tracking-wider text-[10px] text-blue-500">{CHIP_LABELS[k] || k}</span>
              <span className="font-semibold">{
                k === "transportista" && v !== "sin"
                  ? (transportistaOptions.find((t) => t.value === v)?.razon_social || String(v))
                  : (CHIP_VALUES[String(v)] || String(v))
              }</span>
              <span aria-hidden className="text-blue-400">×</span>
            </button>
          ))}
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-800 underline"
            data-testid="btn-clear-filters"
            onClick={() => applyFilters({ ...filtrosIniciales(sinSage) })}
          >
            Limpiar todo
          </button>
        </div>
      ) : null}

      <Card className="p-4 mb-4">
        <div className="flex items-center gap-2 mb-3 label-tag"><Filter className="h-3.5 w-3.5"/> Filtros</div>
        <div className="flex flex-col lg:flex-row lg:flex-nowrap lg:items-end gap-2">
          <div className="w-full max-w-full lg:w-[200px] lg:max-w-[200px] lg:flex-none shrink-0">
            <Field label={"B" + String.fromCharCode(0xfa) + "squeda inteligente"}>
              <SmartSearch
                value={q.buscar}
                onChange={(v) => applyFilters({ ...q, buscar: v })}
                onSubmit={() => {}}
                getSuggestions={getSuggestions}
                onSelect={(s) => applyFilters({ ...q, ...(s.apply_filters || {}), buscar: s.apply_filters?.buscar || "" })}
                testid="filter-buscar"
              />
            </Field>
          </div>
          <div className="w-full lg:w-[70px] shrink-0">
            <Field label="Serie">
              <Input value={q.serie} onChange={(e)=>applyFilters({...q, serie:e.target.value})} placeholder="00" data-testid="filter-serie"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Desde">
              <DatePicker value={q.fecha_desde} onChange={(v)=>applyFilters({...q, fecha_desde:v})} testid="filter-desde"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Hasta">
              <DatePicker value={q.fecha_hasta} onChange={(v)=>applyFilters({...q, fecha_hasta:v})} testid="filter-hasta"/>
            </Field>
          </div>
          {!sinSage ? (
          <div className="w-full lg:w-[112px] shrink-0">
            <Label htmlFor="filter-status" className="label-tag">Facturado</Label>
            <Select value={q.status_facturado || "all"} onValueChange={(v)=>applyFilters({...q, status_facturado: v==="all" ? "" : v })}>
              <SelectTrigger id="filter-status" data-testid="filter-status" className="px-2"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="0">Pendiente</SelectItem>
                <SelectItem value="-1">Facturado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          ) : null}
          {!sinSage ? (
          <div className="w-full lg:w-[124px] shrink-0">
            <Label htmlFor="filter-estado" className="label-tag">Estado</Label>
            <Select value={q.estado || "pendiente"} onValueChange={(v)=>applyFilters({ ...q, estado: v })}>
              <SelectTrigger id="filter-estado" data-testid="filter-estado" className="px-2"><SelectValue placeholder="Pendientes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="usado">Usados en DeCA</SelectItem>
                <SelectItem value="todos">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          ) : null}
          <div className="min-w-0 flex-1 lg:max-w-[240px]">
            <Label htmlFor="filter-transportista" className="label-tag">Transportista</Label>
            <Select
              value={q.transportista || "all"}
              onValueChange={(v) => applyFilters({ ...q, transportista: v === "all" ? "" : v })}
            >
              <SelectTrigger id="filter-transportista" data-testid="filter-transportista" className="px-2 [&>span]:truncate">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="sin">Sin transportista</SelectItem>
                {transportistaOptions.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.razon_social || t.value}{t.nif ? ` · ${t.nif}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600" data-testid="albaranes-counter">
            <span className="font-semibold text-slate-900">{items.length}</span> mostrados
            {filtered.length !== allItems.length ? <span className="text-slate-500"> (de {allItems.length.toLocaleString("es-ES")} cargados)</span> : null}
            {!sinSage && !extraFiltersActive && q.estado === "pendiente" && totalRemote > allItems.length ? <span className="text-slate-500"> · {totalRemote.toLocaleString("es-ES")} en Sage</span> : null}
            {extraFiltersActive ? <span className="text-slate-400"> · filtrado en tabla</span> : !sinSage && q.estado === "pendiente" ? <span className="text-slate-400"> · pendientes</span> : null}
            {" · "}
            <span className="font-semibold text-blue-700">{selectedRows.length}</span> seleccionados
          </div>
          <div className="flex items-center gap-3">
            <Tabs value={modo} onValueChange={setModo}>
              <TabsList data-testid="mode-tabs">
                <TabsTrigger value="unico" data-testid="mode-unico">Un solo DeCA</TabsTrigger>
                <TabsTrigger value="agrupado" data-testid="mode-agrupado">Por destino</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              onClick={irAPreview}
              disabled={selectedRefs.length === 0 || mezclaTransportistas}
              title={mezclaTransportistas ? "No se puede previsualizar albaranes de distintos transportistas" : undefined}
              className="bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50 disabled:pointer-events-auto"
              data-testid="btn-preview"
            >
              Previsualizar <ArrowRight className="h-4 w-4 ml-2"/>
            </Button>
          </div>
        </div>
        {puedeReusar && selectedRows.some(estaUsado) ? (
          <div className="flex items-start gap-2 px-3 py-2 border-b border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="aviso-reutilizar">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0"/>
            <div>
              Hay albaranes seleccionados que ya est{String.fromCharCode(0xe1)}n en un DeCA. Puedes reutilizarlos, pero revisa que no sea un error.
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="deca-table w-full text-left min-w-[900px]">
            <thead>
              <tr>
                <th className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} disabled={selectableItems.length === 0} data-testid="check-all" aria-label="Seleccionar todos"/></th>
                <SortableTh label="Nº / Serie" sortKey="nro" sort={sort} onSort={toggleSort} />
                <SortableTh label="Fecha" sortKey="fecha" sort={sort} onSort={toggleSort} />
                <SortableTh label="Destinatario" sortKey="destinatario" sort={sort} onSort={toggleSort} />
                <SortableTh label="Cargador" sortKey="cargador" sort={sort} onSort={toggleSort} />
                <SortableTh label="Transportista" sortKey="transportista" sort={sort} onSort={toggleSort} />
                <SortableTh label="Bultos" sortKey="bultos" sort={sort} onSort={toggleSort} align="right" />
                <SortableTh label="Peso (kg)" sortKey="peso" sort={sort} onSort={toggleSort} align="right" />
                <SortableTh label="Estado" sortKey="estado" sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const k = keyOf(r);
                const usado = estaUsado(r);
                const manualSage = esManualSage(r);
                const bloqueado = manualSage || (usado && !puedeReusar);
                const check = selected.has(k) && !bloqueado && (puedeReusar || !usado);
                const msgUsado = puedeReusar
                  ? "Este albar" + String.fromCharCode(0xe1) + "n ya est" + String.fromCharCode(0xe1) + " en un DeCA. Puedes reutilizarlo."
                  : "Este albar" + String.fromCharCode(0xe1) + "n ya est" + String.fromCharCode(0xe1) + " en un DeCA y no se puede volver a usar";
                const msgCheck = manualSage ? msgManualSage : (usado ? msgUsado : `Seleccionar albarán ${displayedNro(r)}`);
                return (
                  <tr key={k} data-testid={`row-${k}`} className={bloqueado && !check ? "opacity-60" : check ? "bg-blue-50/50" : ""}>
                    <td>
                      <Checkbox
                        checked={check}
                        disabled={bloqueado}
                        onCheckedChange={() => toggle(r)}
                        data-testid={`check-${k}`}
                        aria-label={msgCheck}
                        title={manualSage || usado ? msgCheck : undefined}
                      />
                    </td>
                    <td>
                      <div className="font-semibold">{displayedNro(r)}</div>
                      <div className="text-xs text-slate-500">
                        Ej. {r.ejercicio}
                        {r.fuente === "manual" ? (
                          <span className="ml-1 text-amber-800" title={sinSage ? undefined : msgManualSage}>
                            {" · Manual"}{sinSage ? "" : " (no est" + String.fromCharCode(0xe1) + " en Sage)"}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-slate-700">{fmtFecha(r.fecha)}</td>
                    <td>
                      <div className="font-medium text-slate-900">{r.cliente.razon_social}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.cliente.nif} · {r.cliente.municipio}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{r.cargador.razon_social}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.cargador.nif}</div>
                    </td>
                    <td>
                      {r.transportista.razon_social || r.transportista.nif ? (
                        <>
                          <div className="font-medium text-slate-900">{r.transportista.razon_social}</div>
                          <div className="text-xs text-slate-500 font-mono">{r.transportista.nif}</div>
                        </>
                      ) : (
                        <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5" title="Sin transportista → art.6 bloquea la generación">— sin transportista</span>
                      )}
                    </td>
                    <td className="text-right font-mono">{fmt(r.bultos)}</td>
                    <td className="text-right font-mono">{fmt(r.peso_bruto)}</td>
                    <td>
                      {r.utilizado_en_deca ? (
                        <span className="status-badge status-modificado" data-testid="badge-utilizado">Usado en DeCA</span>
                      ) : r.status_facturado === -1 ? (
                        <span className="status-badge status-borrador">Facturado</span>
                      ) : (
                        <span className="status-badge status-generado">Pendiente</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && !loading ? (
                <tr><td colSpan={9}>
                  <div className="py-16 text-center text-slate-500 flex flex-col items-center gap-2">
                    <PackageSearch className="h-8 w-8 text-slate-400"/>
                    <div className="font-medium text-slate-700">No hay albaranes con esos filtros</div>
                    <div className="text-sm">
                      {allItems.length > 0
                        ? "Ajusta la b" + String.fromCharCode(0xfa) + "squeda: basta con que el texto est" + String.fromCharCode(0xe9) + " contenido en n" + String.fromCharCode(0xfa) + "mero, cliente, NIF o serie."
                        : source === "manual"
                          ? "Esta empresa a" + String.fromCharCode(0xfa) + "n no tiene albaranes. Usa DeCA manual o activa Sage en el usuario."
                          : "Ajusta la b" + String.fromCharCode(0xfa) + "squeda o pulsa Actualizar para recargar Sage."}
                    </div>
                  </div>
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-t border-slate-200 bg-slate-50" data-testid="pagination-bar">
          <div className="text-xs text-slate-500">
            Página <span className="font-semibold text-slate-900">{safePage}</span>
            {filtered.length > 0 ? <> · {pageCount.toLocaleString("es-ES")} páginas · {filtered.length.toLocaleString("es-ES")} registros</> : null}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={()=>setPage(Math.max(1, safePage-1))} disabled={safePage<=1} data-testid="btn-prev">
              <ChevronLeft className="h-4 w-4 mr-1"/> Anterior
            </Button>
            <span className="text-xs font-mono text-slate-700 px-2">Pág. {safePage}</span>
            <Button variant="outline" size="sm" onClick={()=>setPage(safePage+1)} disabled={!hasMore} data-testid="btn-next">
              Siguiente <ChevronRight className="h-4 w-4 ml-1"/>
            </Button>
          </div>
        </div>
      </Card>

      {mezclaTransportistas ? (
        <Card className="mt-4 p-4 border-red-200 bg-red-50" data-testid="aviso-mezcla-transportistas">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-red-100 border border-red-200"><AlertTriangle className="h-4 w-4 text-red-700"/></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-900">No se puede previsualizar: hay varios transportistas</div>
              <div className="text-xs text-red-800 mt-1">
                Selecciona solo albaranes del mismo transportista. Un DeCA no puede mezclar transportistas distintos.
              </div>
            </div>
          </div>
        </Card>
      ) : clavesInfo.length > 0 ? (
        <Card className="mt-4 p-4 border-blue-200 bg-blue-50/50">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-blue-100 border border-blue-200"><AlertTriangle className="h-4 w-4 text-blue-700"/></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-blue-900">
                {modo === "unico"
                  ? "Se generar" + String.fromCharCode(0xe1) + " 1 DeCA con todos los albaranes seleccionados"
                  : "Se generar" + String.fromCharCode(0xe1) + "n " + clavesInfo.length + " DeCA (uno por cliente / destino)"}
              </div>
              <div className="text-xs text-blue-800 mt-1">
                {modo === "unico"
                  ? "Todos los albaranes van en el mismo documento, aunque tengan destinos distintos."
                  : "Los albaranes del mismo cliente / destino se agrupan. Cada destino distinto genera su DeCA."}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {clavesInfo.map((c, i) => (
                  <div key={i} className="border border-blue-200 bg-white rounded-md px-3 py-2 text-xs">
                    <div className="text-slate-900 font-medium">{c.cliente?.razon_social || c.nro || "Destino"}</div>
                    <div className="font-mono text-slate-500">{c.cliente?.nif || c.nro || ""}</div>
                    <div className="text-slate-600"><b>{fmtEnvios(c.n)}</b></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
